/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable no-console */
import type {FocusEvent, FormEvent, ChangeEvent} from 'react';
import {useEffect, useState} from 'react';
import {Accordion} from 'react-bootstrap';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';

import LayoutMain from 'components/Layouts/LayoutMain';
import {useGetSystemConfigQuery, useUpdateSystemConfigMutation} from 'services';
import {useAppSelector} from 'store/hooks';
import {hasExactRole} from 'utils';

TimeAgo.setDefaultLocale(en.locale);
TimeAgo.addLocale(en);

const TIME_INTERVAL = 59000;

const Configuration = () => {
	const {roles} = useAppSelector(state => state.user);
	const isReadOnly = hasExactRole(roles, 'system_admin_read_only');

	const timeAgo = new TimeAgo('en-US');

	const {data = {}, isLoading = true} = useGetSystemConfigQuery(undefined);
	const sortedData = Object.entries(data).sort((a, b) =>
		a[0].localeCompare(b[0]),
	);

	const [updateSystemConfig, {isLoading: isLoadingUpdate}] =
		useUpdateSystemConfigMutation();

	const enabledEdit = sortedData.map(el => ({
		key: el[0],
		enabledEdit: false,
		value: el[1],
		originalValue: el[1],
	}));
	const [keys, setKeys] = useState(enabledEdit);
	const [timeStr, setTimeStr] = useState<string>('just now');
	const [initialDate, setInitialDate] = useState<Date>(new Date());
	const [editingRow, setEditingRow] = useState<string | null>(null);

	useEffect(() => {
		setKeys(enabledEdit);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	/**
	 * The function logs the name and value of an input element when it is changed.
	 * @param e - ChangeEvent<HTMLInputElement> - This is a type of event that is triggered when the value
	 * of an input element changes. The parameter "e" is an object that contains information about the
	 * event, such as the name and value of the input element that triggered the event.
	 */
	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const tmp = keys.map(el => {
			if (el.key === e.target.name) {
				return {
					...el,
					value: e.target.value,
				};
			}
			return el;
		});
		setKeys(tmp);
	};

	/**
	 * The handleSubmit function is an asynchronous function that handles form submission and updates a
	 * system configuration based on the provided key and value.
	 * @param {FocusEvent<HTMLElement> | FormEvent<HTMLFormElement>} e - The parameter `e` is an event
	 * object that can be either a `FocusEvent` or a `FormEvent`. It represents the event that triggered
	 * the `handleSubmit` function.
	 * @param {number} i - The parameter `i` is of type `number` and is used as an index to access
	 * elements in the `keys` array. It is used to specify which element's `key` and `value` should be
	 * passed to the `updateSystemConfig` function.
	 */
	const handleSubmit = async (
		e: FocusEvent<HTMLElement> | FormEvent<HTMLFormElement>,
		i: number,
	) => {
		e.preventDefault();
		try {
			await updateSystemConfig({
				key: keys[i].key,
				value: keys[i].value,
			}).unwrap();
			setKeys(currentKeys =>
				currentKeys.map((el, index) => {
					if (index === i) {
						return {...el, enabledEdit: false};
					}
					return el;
				}),
			);
			setEditingRow(null);
		} catch (error) {
			console.error(error);
		}
	};

	/**
	 * Toggles editing state for a given key while resetting others.
	 * This function ensures only one field is editable at a time by resetting editing states and values of all fields
	 * except the one identified by the given key. It then toggles the editing state of the targeted field, allowing
	 * it to enter or exit edit mode.
	 * @param {string} key - Identifier for the field to toggle editing state.
	 */
	const handleEnabledInput = (key: string) => {
		const tempKeys = keys.map(el => {
			if (el.key !== key && el.enabledEdit) {
				return {
					...el,
					value: el.originalValue,
					enabledEdit: false,
				};
			}
			return el;
		});
		const updatedKeys = tempKeys.map(el => {
			if (el.key === key) {
				return {
					...el,
					enabledEdit: !el.enabledEdit,
				};
			}
			return el;
		});
		setKeys(updatedKeys);
		setEditingRow(prevEditingRow => (prevEditingRow === key ? null : key));
	};

	const handleCancelEdit = (key: string) => {
		setKeys(
			keys.map(el => {
				if (el.key === key) {
					return {
						...el,
						value: el.originalValue,
						enabledEdit: false,
					};
				}
				return el;
			}),
		);
		setEditingRow(null);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
		}
	};

	// time
	const handleDateAgo = () => {
		setTimeStr(timeAgo.format(initialDate));
	};

	/* A custom hook that is used to update the time every 59 seconds. */
	useEffect(() => {
		const interval = setInterval(() => {
			handleDateAgo();
		}, TIME_INTERVAL);
		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* It's a hook that runs after the component is rendered. */
	useEffect(() => {
		setInitialDate(new Date());
		handleDateAgo();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [keys]);

	return (
		<LayoutMain isLoading={isLoading}>
			<div className="container-fluid" data-testid="configuration">
				<div className="row">
					<div className="col-md-12">
						<h1 className="mt-2">System Configuration</h1>
					</div>
				</div>

				<div className="row mt-4">
					<div className="card-tasks card h-100">
						<div className="card-body mt-3">
							<Accordion defaultActiveKey={['0']} alwaysOpen>
								<Accordion.Item eventKey="0">
									<Accordion.Header className="font-weight-bold">
										General Configuration
									</Accordion.Header>
									<Accordion.Body>
										<div className="card-body table-responsive p-0">
											<table className="table table-striped table-bordered table-head-fixed text-nowrap">
												<thead className="thead-dark">
													<tr>
														<th scope="col">KEY</th>
														<th scope="col">VALUE</th>
													</tr>
												</thead>
												<tbody>
													{keys.map((el, i: number) => (
														<tr key={i}>
															<td>{el.key}</td>
															<td>
																<form
																	onSubmit={e => handleSubmit(e, i)}
																	className="d-flex justify-content-between">
																	{!keys[i]?.enabledEdit ? (
																		<span>{el.value as never}</span>
																	) : (
																		<input
																			type="text"
																			className="form-control"
																			id="title"
																			placeholder="Value"
																			name={el.key}
																			value={el.value as never}
																			onChange={handleChange}
																			onKeyDown={handleKeyDown}
																		/>
																	)}
																	{!isReadOnly && (
																		<>
																			<button
																				type="button"
																				className="border-0 bg-transparent d-inline"
																				onClick={() => {
																					if (editingRow === el.key) {
																						handleCancelEdit(el.key);
																					} else {
																						handleEnabledInput(el.key);
																					}
																				}}
																				onKeyDown={handleKeyDown}
																				disabled={isLoadingUpdate}>
																				<i
																					className={`bi-1 ${
																						editingRow === el.key
																							? 'bi-x fs-5'
																							: 'bi-pencil'
																					}`}
																					title=""
																					data-toggle="tooltip"
																					data-original-title="Search"
																				/>
																			</button>
																			{editingRow === el.key && (
																				<button
																					type="submit"
																					className="border-0 bg-transparent d-inline"
																					onKeyDown={handleKeyDown}
																					disabled={isLoadingUpdate}>
																					<i className="bi-check-circle fs-5" />
																				</button>
																			)}
																		</>
																	)}
																</form>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</Accordion.Body>
								</Accordion.Item>
							</Accordion>
						</div>
						<div className="card-footer">
							<hr />
							<div className="stats">
								<i className="bi-1 bi-clock-history" /> Updated {timeStr}
							</div>
						</div>
					</div>
				</div>
			</div>
		</LayoutMain>
	);
};

export default Configuration;
